import React from 'react';
import { Network, Shield, Layers, Workflow, Zap } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function Features() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useScrollAnimation();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useScrollAnimation({
    threshold: 0.05
  });
  return <section id="features" className="bg-slate-950 py-24 bg-dot-pattern relative" data-id="element-39">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-40">
        <div ref={headerRef} className={`max-w-3xl mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-41">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-100 mb-4 tracking-tight" data-id="element-42">
            Not a replacement.
            <br data-id="element-43" />
            An amplifier.
          </h2>
          <p className="text-xl text-slate-400" data-id="element-44">
            One AI layer. Every tool. All levels of your organization.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-45">
          {/* Row 1 */}
          {/* Card 1 - Wide */}
          <div className={`md:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-indigo-500/50 hover:shadow-[0_0_30px_-10px_rgba(99,102,241,0.3)] transition-all duration-300 flex flex-col justify-between group ${gridVisible ? 'animate-fade-in-up delay-100' : 'opacity-0'}`} data-id="element-46">
            <div data-id="element-47">
              <div className="flex items-center justify-between mb-6" data-id="element-48">
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20" data-id="element-49">
                  <Network className="w-6 h-6 text-indigo-400" data-id="element-50" />
                </div>
                <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-300 border border-slate-700" data-id="element-51">
                  Orchestration Layer
                </span>
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-52">
                Your tools, supercharged
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-md" data-id="element-53">
                The AI connects to Jira, Confluence, Slack, Google, and Notion
                via MCP integrations. Ask "What's the sprint status?" and get a
                real answer — from real data, in seconds.
              </p>
            </div>
            <div className="mt-8 bg-slate-950 rounded-xl p-4 border border-slate-800 relative overflow-hidden" data-id="element-54">
              <div className="flex flex-col gap-3" data-id="element-55">
                <div className="self-end bg-indigo-600/20 border border-indigo-500/30 rounded-lg rounded-tr-none px-4 py-2 text-sm text-indigo-100 max-w-[80%]" data-id="element-56">
                  What's the sprint status?
                </div>
                <div className="self-start bg-slate-800 border border-slate-700 rounded-lg rounded-tl-none px-4 py-3 text-sm text-slate-300 max-w-[90%]" data-id="element-57">
                  <div className="flex items-center gap-2 mb-2" data-id="element-58">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-59"></span>
                    <span className="font-medium text-slate-200" data-id="element-60">
                      Sprint 42 is on track (85% complete).
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs" data-id="element-61">
                    Pulled from Jira & Slack • 2s ago
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 - Tall */}
          <div className={`md:col-span-1 bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-sky-500/50 hover:shadow-[0_0_30px_-10px_rgba(56,189,248,0.3)] transition-all duration-300 flex flex-col group ${gridVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`} data-id="element-62">
            <div className="flex items-center justify-between mb-6" data-id="element-63">
              <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center border border-sky-500/20" data-id="element-64">
                <Shield className="w-6 h-6 text-sky-400" data-id="element-65" />
              </div>
              <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-300 border border-slate-700" data-id="element-66">
                Enterprise-ready
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-67">
              One platform, many organizations
            </h3>
            <p className="text-slate-400 leading-relaxed mb-8" data-id="element-68">
              Each org operates in total isolation. Separate contexts, separate
              credentials, zero data bleed between tenants.
            </p>
            <div className="mt-auto flex justify-center items-center gap-4 py-4" data-id="element-69">
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-slate-700 flex items-center justify-center relative group-hover:border-sky-500/50 transition-colors" data-id="element-70">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400" data-id="element-71">
                  Org A
                </div>
              </div>
              <div className="w-px h-12 bg-slate-800" data-id="element-72"></div>
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-slate-700 flex items-center justify-center relative group-hover:border-sky-500/50 transition-colors" data-id="element-73">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400" data-id="element-74">
                  Org B
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          {/* Card 3 - Normal */}
          <div className={`md:col-span-1 bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-purple-500/50 hover:shadow-[0_0_30px_-10px_rgba(192,132,252,0.3)] transition-all duration-300 flex flex-col group ${gridVisible ? 'animate-fade-in-up delay-300' : 'opacity-0'}`} data-id="element-75">
            <div className="flex items-center justify-between mb-6" data-id="element-76">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20" data-id="element-77">
                <Layers className="w-6 h-6 text-purple-400" data-id="element-78" />
              </div>
              <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-300 border border-slate-700" data-id="element-79">
                Scales with you
              </span>
            </div>
            <h3 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-80">
              Operational. Managerial. Executive.
            </h3>
            <p className="text-slate-400 leading-relaxed mb-8" data-id="element-81">
              From "who has this task?" to portfolio velocity dashboards — one
              agent serves every level of your organization.
            </p>
            <div className="mt-auto flex flex-col gap-2" data-id="element-82">
              <div className="h-8 rounded bg-slate-800/50 border border-slate-700/50 flex items-center px-3 text-xs text-slate-400 group-hover:bg-purple-500/10 transition-colors" data-id="element-83">
                Director: Portfolio ROI
              </div>
              <div className="h-8 rounded bg-slate-800/80 border border-slate-700/80 flex items-center px-3 text-xs text-slate-300 group-hover:bg-purple-500/20 transition-colors w-11/12" data-id="element-84">
                Manager: Sprint Velocity
              </div>
              <div className="h-8 rounded bg-slate-800 border border-slate-700 flex items-center px-3 text-xs text-slate-200 group-hover:bg-purple-500/30 transition-colors w-5/6" data-id="element-85">
                Ops: PR Status
              </div>
            </div>
          </div>

          {/* Card 4 - Wide */}
          <div className={`md:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-emerald-500/50 hover:shadow-[0_0_30px_-10px_rgba(16,185,129,0.3)] transition-all duration-300 flex flex-col justify-between group ${gridVisible ? 'animate-fade-in-up delay-400' : 'opacity-0'}`} data-id="element-86">
            <div className="flex flex-col md:flex-row gap-8 items-start md:items-center justify-between h-full" data-id="element-87">
              <div className="flex-1" data-id="element-88">
                <div className="flex items-center justify-between mb-6" data-id="element-89">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20" data-id="element-90">
                    <Workflow className="w-6 h-6 text-emerald-400" data-id="element-91" />
                  </div>
                  <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-300 border border-slate-700 md:hidden" data-id="element-92">
                    No lock-in
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-93">
                  Adapt without forking
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-94">
                  Customize behavior via prompts, not code. Switch between
                  Agent, Plan, and Ask modes. The base product stays stable for
                  everyone.
                </p>
              </div>

              <div className="w-full md:w-auto bg-slate-950 rounded-xl p-2 border border-slate-800 flex flex-row md:flex-col gap-2" data-id="element-95">
                <div className="flex-1 md:flex-none px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium text-center cursor-pointer" data-id="element-96">
                  Agent Mode
                </div>
                <div className="flex-1 md:flex-none px-4 py-2 rounded-lg hover:bg-slate-800 text-slate-400 text-sm font-medium text-center cursor-pointer transition-colors" data-id="element-97">
                  Plan Mode
                </div>
                <div className="flex-1 md:flex-none px-4 py-2 rounded-lg hover:bg-slate-800 text-slate-400 text-sm font-medium text-center cursor-pointer transition-colors" data-id="element-98">
                  Ask Mode
                </div>
              </div>
            </div>
          </div>

          {/* Row 3 */}
          {/* Card 5 - Full Width */}
          <div className={`md:col-span-1 md:col-span-3 bg-slate-900 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/50 hover:shadow-[0_0_30px_-10px_rgba(245,158,11,0.3)] transition-all duration-300 flex flex-col md:flex-row gap-8 items-center group ${gridVisible ? 'animate-fade-in-up delay-500' : 'opacity-0'}`} data-id="element-99">
            <div className="flex-1" data-id="element-100">
              <div className="flex items-center gap-4 mb-6" data-id="element-101">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center border border-amber-500/20" data-id="element-102">
                  <Zap className="w-6 h-6 text-amber-400" data-id="element-103" />
                </div>
                <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-300 border border-slate-700" data-id="element-104">
                  Self-healing
                </span>
              </div>
              <h3 className="text-2xl font-bold text-slate-100 mb-3" data-id="element-105">
                Bug reported → Bug fixed. For everyone.
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-2xl" data-id="element-106">
                Every reported failure feeds back into the system. Workium AI
                gets smarter with each correction — your feedback benefits the
                whole community.
              </p>
            </div>

            <div className="w-full md:w-1/3 flex items-center justify-between relative px-4" data-id="element-107">
              <div className="absolute top-1/2 left-4 right-4 h-0.5 bg-slate-800 -translate-y-1/2 z-0" data-id="element-108"></div>
              <div className="absolute top-1/2 left-4 right-1/2 h-0.5 bg-amber-500/50 -translate-y-1/2 z-0 group-hover:right-4 transition-all duration-1000" data-id="element-109"></div>

              <div className="relative z-10 w-8 h-8 rounded-full bg-slate-950 border-2 border-slate-700 flex items-center justify-center group-hover:border-amber-500 transition-colors delay-100" data-id="element-110">
                <div className="w-2 h-2 rounded-full bg-slate-500 group-hover:bg-amber-400" data-id="element-111"></div>
              </div>
              <div className="relative z-10 w-8 h-8 rounded-full bg-slate-950 border-2 border-slate-700 flex items-center justify-center group-hover:border-amber-500 transition-colors delay-300" data-id="element-112">
                <div className="w-2 h-2 rounded-full bg-slate-500 group-hover:bg-amber-400" data-id="element-113"></div>
              </div>
              <div className="relative z-10 w-8 h-8 rounded-full bg-slate-950 border-2 border-slate-700 flex items-center justify-center group-hover:border-amber-500 transition-colors delay-500" data-id="element-114">
                <div className="w-2 h-2 rounded-full bg-slate-500 group-hover:bg-amber-400" data-id="element-115"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}